'use client';

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { LawFirmUser } from '@/lib/api/account-api';
import { getUserDisplayName } from '@/lib/utils/permissions';

interface UserMultiSelectDropdownProps {
  users: LawFirmUser[];
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  excludeUserIds?: string[]; // Users to exclude from selection (e.g., already selected as solicitor)
  loading?: boolean;
  error?: string | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  disableBadge?: boolean;
  buttonClassName?: string; // Custom className for the trigger button
  maxDisplay?: number; // Maximum badges to display before showing count
  showUserDetails?: boolean; // Show email/role under each user's name (default true)
}

export function UserMultiSelectDropdown({
  users,
  selectedUserIds,
  onSelectionChange,
  excludeUserIds = [],
  loading = false,
  error = null,
  placeholder = 'Select team members...',
  disabled = false,
  disableBadge = false,
  className,
  buttonClassName,
  maxDisplay = 3,
  showUserDetails = true
}: UserMultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Find the currently selected users
  const selectedUsers = useMemo(
    () => users.filter((user) => selectedUserIds.includes(user.id.toString())),
    [users, selectedUserIds]
  );

  // Filter available users (exclude already selected and excluded users)
  const availableUsers = useMemo(() => {
    if (!searchTerm && excludeUserIds.length === 0) return users;

    let filtered = users.filter(
      (user) => !excludeUserIds.includes(user.id.toString())
    );

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        const fullName = getUserDisplayName(user).toLowerCase();
        const email = user.email.toLowerCase();
        const role = (user.role || '').toLowerCase();

        return (
          fullName.includes(lowerSearch) ||
          email.includes(lowerSearch) ||
          role.includes(lowerSearch)
        );
      });
    }

    return filtered;
  }, [users, searchTerm, excludeUserIds]);

  const handleSelect = (user: LawFirmUser) => {
    const userId = String(user.id || '');
    if (!userId) return; // Skip if invalid ID

    if (selectedUserIds.includes(userId)) {
      // Remove user
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      // Add user
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  const removeUser = (userId: string) => {
    if (!userId) return; // Skip if invalid ID
    onSelectionChange(selectedUserIds.filter((id) => id !== userId));
  };

  const clearAll = () => {
    onSelectionChange([]);
    setSearchTerm('');
  };

  if (error) {
    return (
      <Alert className="border-destructive">
        <AlertDescription className="text-destructive">
          {error}
        </AlertDescription>
      </Alert>
    );
  }

  // Display selected users as badges
  const displayedUsers = selectedUsers.slice(0, maxDisplay);
  const remainingCount = selectedUsers.length - maxDisplay;

  return (
    <div className="space-y-2">
      {/* Selected users as badges */}
      {!disableBadge && selectedUsers.length > 0 && (
        <div className="flex flex-wrap">
          {displayedUsers.map((user) => (
            <Badge key={String(user.id || '')} variant="secondary">
              {user.firstName || ''} {user.lastName || ''}
              <button
                type="button"
                onClick={() => removeUser(String(user.id || ''))}
                className="text-muted-foreground hover:text-foreground ml-1"
                disabled={disabled}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {remainingCount > 0 && (
            <Badge variant="outline">+{remainingCount} more</Badge>
          )}
        </div>
      )}

      {/* Dropdown */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              'bg-input-bg hover:bg-input-bg-hover h-auto w-full justify-between rounded-md border-0 px-3 py-1.5',
              buttonClassName || className
            )}
            disabled={disabled || loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-muted-foreground">Loading...</span>
              </div>
            ) : selectedUsers.length > 0 ? (
              <span className="text-left">
                {selectedUsers.length} team member
                {selectedUsers.length > 1 ? 's' : ''} selected
              </span>
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search team members..."
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              <CommandEmpty>
                {availableUsers.length === 0
                  ? 'No team members available'
                  : 'No team members found'}
              </CommandEmpty>
              <CommandGroup>
                {availableUsers.map((user) => {
                  const userId = String(user.id || '');
                  const isSelected = selectedUserIds.includes(userId);
                  const isExcluded = excludeUserIds.includes(userId);
                  const fullName =
                    `${user.firstName || ''} ${user.lastName || ''}`.trim();
                  // Name-only mode has no email/role line to fall back on, so
                  // a nameless (e.g. not-yet-accepted invite) user still
                  // needs to show something identifiable.
                  const displayName = showUserDetails
                    ? fullName
                    : fullName || user.email || '';

                  return (
                    <CommandItem
                      key={userId}
                      value={`${user.firstName || ''} ${user.lastName || ''} ${user.email || ''}`}
                      onSelect={() => !isExcluded && handleSelect(user)}
                      className={cn(
                        'cursor-pointer',
                        isExcluded && 'cursor-not-allowed opacity-50'
                      )}
                      disabled={isExcluded}
                    >
                      <div className="flex w-full items-center gap-2">
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="font-medium">{displayName}</span>
                          {showUserDetails && (
                            <span className="text-muted-foreground truncate text-xs">
                              {user.email || ''}
                            </span>
                          )}
                          {showUserDetails && user.role && (
                            <span className="text-muted-foreground text-xs">
                              {user.role}
                            </span>
                          )}
                          {isExcluded && (
                            <span className="text-muted-foreground text-xs">
                              (Already assigned as solicitor)
                            </span>
                          )}
                        </div>
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </div>
                    </CommandItem>
                  );
                })}
                {selectedUsers.length > 0 && (
                  <CommandItem
                    value="clear-all"
                    onSelect={clearAll}
                    className="text-muted-foreground"
                  >
                    <span className="text-sm">Clear all selections</span>
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
